import React, { useState, useEffect, useCallback } from "react";
import { exportarExcel } from "../../functions/exportarExcel";
import { FaFileExcel } from "react-icons/fa";
import { DatePicker, Select, Button, Switch } from "antd";
import locale from "antd/es/date-picker/locale/pt_BR";
import "./correcao.css";
import { toast } from "react-toastify";
import { request } from "../../functions/request";
import { Content } from "antd/es/layout/layout";
import { rangePresets } from "../../constants/constants";
import { BiTable } from "react-icons/bi";
import CorrecaoRelatorio from "./CorrecaoRelatorio";
import CorrecaoRelatorioMesclado from "./CorrecaoRelatorioMesclado";
import { Consult } from "../../components/Consult";
import { useForm } from "antd/es/form/Form";
import { mapLabelToName } from "./Correcao";

const { RangePicker } = DatePicker;

// Página dedicada só pro relatório (leitura), separada da tela de
// Correções (que também permite editar cotações) — tem sua própria rota
// e item de menu.
export default function CorrecaoRelatorioPage() {
  const [ufList, setUfList] = useState();
  const [optUfList, setOptUfList] = useState([]);
  const [cotacao, setCotacao] = useState([]);
  const [dataRange, setDataRange] = useState([]);
  const [optCommo, setOptCommo] = useState();
  const [commList, setCommList] = useState([]);
  const [queries, setQueries] = useState([]);
  const [mesclar, setMesclar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [form] = useForm();
  // Tabelas já carregadas na tela, por id da cotação (ou "mesclado"),
  // entregues pelos componentes de relatório para a exportação em Excel.
  const [exportaveis, setExportaveis] = useState({});
  const [exportando, setExportando] = useState(false);
  const receberDados = useCallback((chave, dados) => {
    setExportaveis((atual) => ({ ...atual, [chave]: dados }));
  }, []);

  const handleElementos = (values) => {
    const generatedQueries = values.quotation.map((cotId) => {
      const commodity = commList.find((item) => item.id === cotId);
      const { ordem, nome } = commodity;

      let queryParams = `?data_start=${encodeURIComponent(
        values.dates[0].$d
      )}&data_final=${encodeURIComponent(values.dates[1].$d)}`;
      queryParams += `&ordem=${encodeURIComponent(ordem)}`;
      if (values.state) {
        queryParams += `&uf=${encodeURIComponent(values.state)}`;
      }

      return {
        id: cotId,
        query: queryParams,
        nome,
      };
    });

    setExportaveis({});
    setQueries(generatedQueries);
  };

  useEffect(() => {
    request("/correcoes/getAllCommodityGraf/", {
      method: "GET",
    })
      .then((response) => {
        const optionsCorrecoes = response.reduce((acc, item) => {
          const mappedLabel = mapLabelToName(item.tipo);

          const existingLabel = acc.find(
            (group) => group.label === mappedLabel
          );

          if (existingLabel) {
            existingLabel.options.push({
              value: item.id,
              label: item.nome,
            });
          } else {
            acc.push({
              label: mappedLabel,
              options: [{ value: item.id, label: item.nome }],
            });
          }

          return acc;
        }, []);
        setCommList(response);
        setOptCommo(optionsCorrecoes);
      })
      .catch((error) => {
        console.error("Erro na requisição:", error);
      });
  }, []);

  useEffect(() => {
    const commodity = commList.find((item) => item.id === cotacao[0]);
    if (commodity) {
      const { ordem } = commodity;
      setIsLoading(true);
      request(`/correcoes/getUfByOrdem/${ordem}`, {
        method: "GET",
      })
        .then((response) => {
          setUfList(response);
          const ufOptions = response.map((item) => ({
            value: item.uf,
            label: item.uf,
          }));

          if (ufOptions.length > 0) {
            setOptUfList(() => [
              { value: "TODOS", label: "Todos" },
              ...ufOptions,
              { value: "RG", label: "Porto do Rio Grande" },
              { value: "ST", label: "Porto de Santos" },
            ]);
          } else {
            setOptUfList([]);
            form.setFieldValue("state", "");
          }
        })
        .finally(() => {
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Erro na requisição:", error);
          toast.error("Ocorreu um erro inesperado! Tente novamente.");
        });
    }
  }, [cotacao]);

  // O que está na tela: a tabela mesclada, ou uma aba por cotação na
  // ordem em que foram escolhidas (só as que já terminaram de carregar).
  function abasParaExportar() {
    if (mesclar) return exportaveis.mesclado ? [exportaveis.mesclado] : [];
    return queries.map((q) => exportaveis[q.id]).filter(Boolean);
  }

  async function exportar() {
    const abas = abasParaExportar();
    if (!abas.length) return;
    setExportando(true);
    try {
      const [ini, fim] = form.getFieldValue("dates") || [];
      const periodo = ini && fim ? `_${ini.format("YYYY-MM-DD")}_a_${fim.format("YYYY-MM-DD")}` : "";
      await exportarExcel(`relatorio_cotacoes${periodo}`, abas);
      if (!mesclar && abas.length < queries.length) {
        toast.warning(`${queries.length - abas.length} cotação(ões) ainda carregando ficaram de fora.`);
      }
    } catch (erro) {
      console.error(erro);
      toast.error("Não foi possível gerar o Excel.");
    } finally {
      setExportando(false);
    }
  }

  return (
    <Consult.Root panelSizes={["30%", "auto"]}>
      <Consult.Form
        form={form}
        title={"Relatório de Cotações"}
        variant="filled"
        bordered
        onFinish={(values) => {
          handleElementos(values);
        }}
        extra={
          <Content
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              justifyContent: "end",
            }}
          >
            <Content style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Switch
                checked={mesclar}
                onChange={setMesclar}
                size="small"
              />
              <span style={{ fontSize: 13 }}>Mesclar em uma tabela</span>
            </Content>
            <Button
              type="primary"
              icon={<BiTable />}
              htmlType="submit"
            >
              Gerar Relatório
            </Button>
            <Button
              icon={<FaFileExcel />}
              disabled={!abasParaExportar().length}
              loading={exportando}
              onClick={exportar}
              title="Exporta as tabelas geradas (uma aba por cotação)"
            >
              Exportar Excel
            </Button>
          </Content>
        }
        style={{ width: "100%" }}
      >
        <Consult.Field
          label={"Cotação"}
          name={"quotation"}
          rules={[{ required: true, message: "Escolha uma Cotação!" }]}
          children={
            <Select
              showSearch
              style={{ width: "100%" }}
              mode="multiple"
              optionFilterProp="children"
              placeholder="Selecione uma ou mais Cotações"
              filterOption={(input, option) =>
                (option?.label.toLocaleLowerCase() ?? "").includes(
                  input.toLocaleLowerCase()
                )
              }
              options={optCommo}
              value={cotacao}
              onChange={setCotacao}
              autoClearSearchValue={false}
            />
          }
        />
        <Consult.Field
          label={"Data Inicial e Final"}
          name={"dates"}
          rules={[
            { required: true, message: "Selecione uma data inicial e final!" },
          ]}
          hasFeedback
          children={
            <RangePicker
              style={{ width: "100%" }}
              presets={rangePresets}
              onChange={(dates) => setDataRange(dates)}
              locale={locale}
              value={dataRange}
            />
          }
        />
        <Consult.Field
          label={"Estado"}
          name={"state"}
          span={4}
          children={
            <Select
              showSearch
              style={{ width: "100%" }}
              optionFilterProp="children"
              placeholder="Selecione um estado"
              filterOption={(input, option) =>
                (option?.label.toLocaleLowerCase() ?? "").includes(
                  input.toLocaleLowerCase()
                )
              }
              disabled={optUfList <= 0}
              loading={isLoading}
              options={optUfList}
              allowClear={true}
            />
          }
        />
      </Consult.Form>
      <Content
        style={{
          width: "100%",
          height: "100%",
          overflowY: "scroll",
          display: mesclar ? "block" : "grid",
          gridTemplateColumns: mesclar ? undefined : "1fr 1fr",
        }}
      >
        {queries.length > 0 &&
          (mesclar ? (
            <CorrecaoRelatorioMesclado queries={queries} allUfs={ufList} onDados={receberDados} />
          ) : (
            queries.map(({ id, query }) => (
              <CorrecaoRelatorio key={id} id={id} query={query} allUfs={ufList} onDados={receberDados} />
            ))
          ))}
      </Content>
    </Consult.Root>
  );
}
